import { makeAutoObservable } from 'mobx'
import { type IDropdownItem } from 'shared/ui'
import { type IUser } from 'entities/Users'
import type { IEntitiesStore } from 'entities/EntitiesExport'

export class NewInboxStore {
  private _newInboxName = ''
  private _activeInboxOwner: number | null = null
  private _selectedMembers: IDropdownItem[] = []
  private _nameError = false

  constructor(private _entities: IEntitiesStore) {
    this._activeInboxOwner = this.resolveDefaultOwner()
    makeAutoObservable(this)
  }

  private resolveDefaultOwner = (): number | null => {
    const nonObserverMembers = this._entities.usersStore.membersList.filter(
      (member) => member.role !== 'view_only'
    )

    return nonObserverMembers.length === 1 ? nonObserverMembers[0].id : null
  }

  get newInboxName() {
    return this._newInboxName
  }

  get activeInboxOwner() {
    return this._activeInboxOwner
  }

  get activeInboxOwnerInfo() {
    return this._entities.usersStore.getItem(this._activeInboxOwner)
  }

  get selectedMembers() {
    return this._selectedMembers
  }

  get selectedMembersIds() {
    return this._selectedMembers.map((item) => String(item.id))
  }

  get members(): IDropdownItem[] {
    return this._entities.usersStore.membersDropdownItems
  }

  get membersWithoutActiveInboxOwner() {
    return this.members.filter((item) => item.id !== this.activeInboxOwner)
  }

  get membersWithoutObserver() {
    return this.members.filter(({ data }) => {
      const user = data as IUser
      return user.role !== 'view_only'
    })
  }

  get newInboxNameTrimmed() {
    return this.newInboxName.trim()
  }

  get nextDisabled() {
    const noName = this.newInboxNameTrimmed.length < 3
    const noOwner = this.membersWithoutObserver.length > 1 && !this.activeInboxOwner

    if (noName || noOwner) {
      return true
    }

    return false
  }

  get hasNameError() {
    return this._nameError && this.newInboxNameTrimmed.length < 3
  }

  setNewInboxName = (value: string) => {
    this._newInboxName = value
    this._nameError = false
  }

  setActiveInboxOwner = (value: number) => {
    this._activeInboxOwner = value
  }

  setSelectedMembers = (value: IDropdownItem) => {
    this._selectedMembers = [...this._selectedMembers, value]
  }

  deleteSelectedMembers = (value: IDropdownItem) => {
    this._selectedMembers = this._selectedMembers.filter((item) => item.id !== value.id)
  }

  reset = () => {
    this._newInboxName = ''
    this._activeInboxOwner = this.resolveDefaultOwner()
    this._selectedMembers = []
    this._nameError = false
  }

  validateName = () => {
    this._nameError = this.newInboxNameTrimmed.length < 3
  }
}
